/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { KeyboardEvent, useState } from 'react'
import { observer } from 'mobx-react'
import { Checkbox, Col, Form, Input, Row } from 'antd'
import { FormComponentProps } from 'antd/lib/form'
import { HASH_PARAMETER } from '@constants/index'
import GoogleLogin from 'react-google-login'
import _ from 'lodash'
import { isMobile } from 'react-device-detect'
import { Helmet } from 'react-helmet'
import * as Utils from '@utils/utils'
import { GlobalStoreKey } from '@v2/constants/common'
import StorageUtils from '@utils/storage-utils'
import useRootStore from '@store/useRootStore'
import { useOnMount, useOnUnmount } from '@utils/reactExt'
// icon
import { GoogleIco, SpeedIco } from '@shared/Icon/LoginIcon'
import { LogoWingTxtIco } from '@shared/Icon/LogoIcon'
import { IdIco, PwIco } from '@shared/Icon/EtcIcon'
// css
import { Button } from '@styles/button.style'
import { ISignin } from '@protocol/auth'
import { ACCOUNT_TYPE } from '@interface/common'
import {
  FormStyle,
  LeftBtnStyle,
  LeftIconStyle,
  LoginButtonStyle,
  LoginPopupWrap,
  LoginTxt,
  LoginWrap,
  LogoStyle,
  RightBtnStyle,
  SocialBtnStyle,
  SocialLogin,
  SocialLoginRealBtnWrap,
} from './index.style'

const android = window.android

function PopupLogin({ form }: FormComponentProps) {
  const lastSign = StorageUtils.getSignType()
  const userId = StorageUtils.getUserId()
  const [passwordField, setPasswordField] = useState('password')
  const [isPassword, setIsPasswordField] = useState(false)
  const [username, setUsername] = useState(userId)
  const [checked, setChecked] = useState(lastSign === 'local' && !!userId)
  const googleClientId = process.env.GOOGLE_CLIENT_ID
  const naverClientId = process.env.NAVER_CLIENT_ID

  const { authStore, routerStore, joinStore, componentStore, globalStore} = useRootStore()

  const isMobileApp = window.navigator.userAgent.indexOf('ADV_1') >= 0
  const isAndroidInAppBrowser = Utils.isAndroidInAppBrowser()
  const isIosNaverInAppBrowser = Utils.isIosNaverInAppBrowser()
  // const isKakaoJoinBlock = globalStore.isAccountTransferEnabled()

  const naverLoginRealBtnWrapRef = React.createRef<HTMLDivElement>()
  const frontendPortWithColon = process.env.FRONTEND_PORT ? `:${process.env.FRONTEND_PORT}` : ''

  let naverLoginCallbackUrl = process.env.FRONTEND_BASE_URL + frontendPortWithColon
  if (!_.isEmpty(_.trim(process.env.FRONTEND_SECOND_BASE_URL))
    && window.location.href.indexOf(process.env.FRONTEND_SECOND_BASE_URL) > -1) {
    naverLoginCallbackUrl = process.env.FRONTEND_SECOND_BASE_URL + frontendPortWithColon
  }

  // img
  const imageUrl = globalStore.findGlobalSetting(GlobalStoreKey.INGAME_IMAGES_URL)?.value ?? ''
  const ClearIcoImgUrl = `${imageUrl}icon/login_icon/login_clear_icon.png`
  const EyeIcoImgUrl = `${imageUrl}icon/login_icon/login_pw_eye.png`
  const EyeXIcoImgUrl = `${imageUrl}icon/login_icon/login_pw_eye_x.png`
  const NaverLogoImgUrl = `${imageUrl}icon/login_icon/naver_logo.png`
  // const KakaoLogoImgUrl = `${imageUrl}icon/login_icon/kakao_logo.png`
  const AppleLogoImgUrl = `${imageUrl}icon/login_icon/apple_logo.png`

  const Clear = <img key="login_clear_icon" src={ClearIcoImgUrl} alt="지우기 버튼" css={[LeftIconStyle, { width: 24 }]}/>
  const Eye = <img src={EyeIcoImgUrl} alt="비밀번호 보이기" css={[LeftIconStyle, { width: 24 }]}/>
  const EyeX = <img key="login_pw_eye_x" src={EyeXIcoImgUrl} alt="비밀번호 감추기" css={[LeftIconStyle, { width: 24 }]}/>

  const appleLoginEnabled = globalStore.getAppleLoginEnabled()

  const Logo = (
    <LogoStyle>
      <LogoWingTxtIco/>
    </LogoStyle>
  )
  const pathName = routerStore.location.pathname
  if (pathName) {
    const [, pageName] = pathName.split('/')
    if (pageName === 'login') {
      authStore.setIsForceShowLoginPopup(true)
      Utils.loadOrReload('/', false)
      return
    }
  }

  useOnMount(() => {
    const naverLoginRealBtn = naverLoginRealBtnWrapRef.current.getElementsByTagName('a')
    if (_.isEmpty(naverLoginRealBtn) && window.naverLogin) {
      window.naverLogin.init()
    }
  })

  const goPage = (arg: number) => {
    const isSamsung = navigator.userAgent.toLowerCase().indexOf('samsungbrowser') !== -1
    const hashParametersWithQuestionPrefix = Utils.getAttachableHashParameters(true)
    const hashParameters = Utils.getAttachableHashParameters(false)

    const target = isMobile && !isSamsung ? 'self' : 'popup'

    if (arg === 0) {
      let findIdUrl: string

      if (isMobile) {
        findIdUrl = `${process.env.HOMEPAGE_MO}/#/find_id${hashParametersWithQuestionPrefix}`

        if (isAndroidInAppBrowser) {
          goToAndroidBaseBrowser(findIdUrl)
          return
        }

        if (android) {
          android.jnRequestOpenBrowser(`${process.env.HOMEPAGE_MO}/#/find_id${hashParametersWithQuestionPrefix}`)
          return
        }

        if (isIosNaverInAppBrowser) {
          goToIosChromeBrowser(findIdUrl)
          return
        }
      } else {
        findIdUrl = `${process.env.HOMEPAGE_PC}/#/main?showFindPopup=id${hashParameters}`
      }

      if (findIdUrl) {
        globalStore.toMoveWebHomePage(findIdUrl, target)
      }
    } else if (arg === 1) {
      let findPasswordUrl

      if (isMobile) {
        findPasswordUrl = `${process.env.HOMEPAGE_MO}/#/find_pw${hashParametersWithQuestionPrefix}`

        if (isAndroidInAppBrowser) {
          goToAndroidBaseBrowser(findPasswordUrl)
          return
        }

        if (android) {
          android.jnRequestOpenBrowser(findPasswordUrl)
          return
        }

        if (isIosNaverInAppBrowser) {
          goToIosChromeBrowser(findPasswordUrl)
          return
        }
      } else {
        findPasswordUrl = `${process.env.HOMEPAGE_PC}/#/main?showFindPopup=pw${hashParameters}`
      }

      if (findPasswordUrl) {
        globalStore.toMoveWebHomePage(findPasswordUrl, target)
      }
    } else if (arg === 2) {
      if (isAndroidInAppBrowser) {
        goToAndroidBaseBrowser()
        return
      }

      if (isIosNaverInAppBrowser) {
        goToIosChromeBrowser()
        return
      }

      globalStore.setVisibleSideBar(false)
      globalStore.resetGeneralPopupProperties()

      joinStore.setAccountType(ACCOUNT_TYPE.LOCAL)
      Utils.loadOrReload(`/agree${Utils.getAllHashParameters(true)}`)
    }
  }

  // functions
  const doLogin = (values: ISignin.Params) => {
    if (checked) {
      StorageUtils.saveSignTypeAndUserId(ACCOUNT_TYPE.LOCAL, values.username)
    } else {
      StorageUtils.removeSignTypeAndUserId()
    }
    authStore.login(values)
  }

  const doLoginByGoogle = (values: ISignin.SocialLoginParams) => {
    if (checked) {
      StorageUtils.saveSignType(ACCOUNT_TYPE.GOOGLE)
      StorageUtils.removeUserId()
    } else {
      StorageUtils.removeSignTypeAndUserId()
    }
    authStore.loginByGoogle(values)
  }

  // const doLoginByKakao = (values: ISignin.SocialLoginParams) => {
  //   if (checked) {
  //     StorageUtils.saveSignType(ACCOUNT_TYPE.KAKAO)
  //     StorageUtils.removeUserId()
  //   } else {
  //     StorageUtils.removeSignTypeAndUserId()
  //   }
  //   authStore.loginByKakao(values)
  // }

  const handleMessage = event => {
    const { data = {} } = event
    const { type, key, value = {} } = data
    if (type === 'nj') {
      if (key === 'njGoogleSignIn') {
        const { msg } = value
        const msgObj = JSON.parse(msg)
        const params = {
          username: msgObj.id,
          password: 'null',
          email: msgObj.email,
        }
        doLoginByGoogle(params)
      } else if (key === 'njGoogleSignOut') {
        if (!_.isNil(authStore.memberInfo)) {
          authStore.logout()
        }
      }
    } else if (type === 'appleAuthResult') {
      authStore.loginByApple({ ...value, password: 'null' })
    }
  }

  useOnMount(() => {
    // 아래의 cleanup을 호출 하면 시나리오상 맞지 않아서 제거
    // matchupStore.cleanup()
    // globalStore.cleanup()
    const user = StorageUtils.getUserId()
    setUsername(user)

    if (checked && username && username !== 'undefined') {
      form.setFieldsValue({ username })
      setChecked(true)
    } else {
      form.setFieldsValue({ username: '' })
      setChecked(false)
    }
    window.addEventListener('message', handleMessage)
  })

  useOnUnmount(() => {
    window.removeEventListener('message', handleMessage)
  })

  // keyboard event 와 form event 동시에 받을 수 있게 any 로 만듬
  const handleSubmit = (e: React.SyntheticEvent<any, any>) => {
    e.preventDefault()

    if (isAndroidInAppBrowser) {
      goToAndroidBaseBrowser()
      return
    }

    if (isIosNaverInAppBrowser) {
      goToIosChromeBrowser()
      return
    }

    form.validateFields((err: any, values: any) => {
      if (!err) {
        doLogin(values)
      }
    })
  }

  const { getFieldDecorator } = form

  const togglePasswordField = e => {
    e.preventDefault()
    if (isPassword) {
      setPasswordField('password')
    } else {
      setPasswordField('text')
    }
    setIsPasswordField(!isPassword)
  }

  const responseGoogle = response => {
    const { error } = response
    if (error) {
      console.dir(response)
      return
    }

    const loginParams = {
      username: response.googleId,
      password: 'null',
      email: response.profileObj.email,
    }
    doLoginByGoogle(loginParams)
  }

  const goToAndroidBaseBrowser = (url?: string) => {
    let targetUrl = window.location.href
    if (!_.isEmpty(url)) {
      targetUrl = url
    }
    let intentUrl

    if (Utils.isAndroidNaverInAppBrowser()) {
      intentUrl = `intent://${targetUrl.replace(/^https?:\/\//, '')}#Intent;scheme=http;package=com.android.chrome;end`
    } else {
      intentUrl = `intent://${targetUrl.replace(/^https?:\/\//, '')}#Intent;scheme=http;end`
    }

    if (!_.isEmpty(intentUrl)) {
      window.location.href = intentUrl
    }
  }

  const goToIosChromeBrowser = (url?: string) => {
    let targetUrl = window.location.href
    if(url) {
      targetUrl = url
    }

    window.location.href = targetUrl.replace(/^https?:\/\//, 'googlechrome://')
  }

  const onChangeUserIdSaveState = () => setChecked(!checked)

  const onKeyDownUserIdField = event => {
    if (event.keyCode === 13) {
      event.preventDefault()
    }
  }

  const onClickUserIdClearButton = event => {
    event.preventDefault()
    form.setFieldsValue({ username: '' })
  }

  const onKeyDownPasswordField = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.keyCode === 13) {
      event.preventDefault()
      handleSubmit(event)
    }
  }

  const onClickPasswordClearButton = event => {
    event.preventDefault()
    form.setFieldsValue({ password: '' })
  }

  const onClickGoogleLogInButton = e => {
    e.preventDefault()

    if (isAndroidInAppBrowser) {
      goToAndroidBaseBrowser()
      return
    }

    if (android) {
      if (android) {
        android.jnGoogleSignIn()
      }
    }

    if (isIosNaverInAppBrowser) {
      goToIosChromeBrowser()
      return
    }
  }

  const onClickNaverLoginRealButton = e => {
    console.log('[naverLogin] naver login real button click')
    e.preventDefault()
  }

  const onClickNaverLogin = e => {
    e.preventDefault()

    if (Utils.isNotSupportedBrowser()) {
      globalStore.setVisibleSideBar(false)
      globalStore.resetGeneralPopupProperties()
      authStore.showNotSupportedBrowserDialog()
      return
    }

    if (isAndroidInAppBrowser) {
      goToAndroidBaseBrowser()
      return
    }

    if (isIosNaverInAppBrowser) {
      goToIosChromeBrowser()
      return
    }

    const naverLoginRealBtn = naverLoginRealBtnWrapRef.current.getElementsByTagName('a')
    if (_.isEmpty(naverLoginRealBtn)) {
      console.error('naver login button not initialized.')
      return
    }
    naverLoginRealBtn[0].click()
  }

  // const onClickKakaoLogin = e => {
  //   e.preventDefault()
  //
  //   if (Utils.isNotSupportedBrowser()) {
  //     globalStore.setVisibleSideBar(false)
  //     globalStore.resetGeneralPopupProperties()
  //     authStore.showNotSupportedBrowserDialog()
  //     return
  //   }
  //
  //   const isAccountTransferExpired = globalStore.isAccountTransferExpired()
  //
  //   if (isAccountTransferExpired) {
  //     DialogUtils.showKakaoServiceTerminationInfo()
  //     return
  //   }
  //
  //   window.Kakao.Auth.login({
  //     success(result) {
  //       // globalStore.resetGeneralPopupProperties()
  //       window.Kakao.Auth.setAccessToken(result.access_token)
  //       window.Kakao.API.request({
  //         url: '/v2/user/me',
  //         success(user) {
  //           const params = {
  //             username: user.id,
  //             password: 'null',
  //             email: user.kakao_account.email,
  //           }
  //           doLoginByKakao(params)
  //         },
  //         fail(err) {
  //           console.log('kakao user info fail', err)
  //         },
  //       })
  //     },
  //     fail(err) {
  //       console.log('kakao auth fail.')
  //     },
  //   })
  // }

  const onClickAppleLogin = e => {
    e.preventDefault()

    if (Utils.isNotSupportedBrowser()) {
      globalStore.setVisibleSideBar(false)
      globalStore.resetGeneralPopupProperties()
      authStore.showNotSupportedBrowserDialog()
      return
    }

    if (isAndroidInAppBrowser) {
      goToAndroidBaseBrowser()
      return
    }

    if (isIosNaverInAppBrowser) {
      goToIosChromeBrowser()
      return
    }

    const port = process.env.FRONTEND_PORT ? `:${process.env.FRONTEND_PORT}` : ''
    const url = `${process.env.FRONTEND_BASE_URL}${port}/#/?${HASH_PARAMETER.APPLE_AUTH.KEY}=true`
    window.open(url, 'adventurer_apple_login')
  }

  return (
    <LoginPopupWrap>
      {/*<Dialog />*/}
      <LoginWrap>
        <p className="version">{componentStore.getAppVersion()}</p>
        {Logo}
        <Form onSubmit={handleSubmit} css={FormStyle}>
          <Form.Item className="id">
            {getFieldDecorator('username', {
              rules: [{ required: true, message: '아이디를 입력해주세요' }],
            })(<Input placeholder="아이디를 입력해주세요." onKeyDown={onKeyDownUserIdField}/>)}
            <IdIco/>
          </Form.Item>
          <Form.Item className="pw">
            {getFieldDecorator('password', {
              rules: [{ required: true, message: '비밀번호를 입력해주세요.' }],
            })(
              <Input
                type={passwordField}
                placeholder="비밀번호를 입력해주세요."
                onKeyDown={onKeyDownPasswordField}
              />,
            )}
            <PwIco/>
          </Form.Item>

          <Button TP css={RightBtnStyle} onClick={onClickUserIdClearButton} className="ant-form-item-btn id">
            {Clear}
          </Button>

          {/* 비번 보일때 안보이게 하는 버튼 */}
          <Button TP css={LeftBtnStyle} onClick={togglePasswordField} className="ant-form-item-btn password">
            {isPassword ? EyeX : Eye}
          </Button>
          <Button TP css={RightBtnStyle} onClick={onClickPasswordClearButton} className="ant-form-item-btn password">
            {Clear}
          </Button>
          <Form.Item className="id-save">
            <Checkbox css={{ float: 'left' }} defaultChecked={checked} onChange={onChangeUserIdSaveState}>
              아이디 저장
            </Checkbox>
          </Form.Item>
          <Form.Item>
            <Button navy css={LoginButtonStyle}>
              승부사 ID 로그인
            </Button>

            <Row type="flex" justify="space-around" className="row">
              <Col>
                <LoginTxt id="findId" className="cursor-pointer" onClick={() => {
                  goPage(0)
                }}>아이디 찾기</LoginTxt>
              </Col>
              <Col>
                <LoginTxt id="findPw" className="cursor-pointer" onClick={() => {
                  goPage(1)
                }}>비밀번호 찾기</LoginTxt>
              </Col>
              <Col>
                <LoginTxt id="signUp" className="cursor-pointer" onClick={() => {
                  goPage(2)
                }}>회원가입</LoginTxt>
              </Col>
            </Row>

            <SocialLogin>
              <p className="social-login-txt">
                <SpeedIco/>
                스피드 로그인
              </p>
              <div className="social-btn-wrap">
                {/* 네이버/카카오/구글 로그인 */}
                <Button
                  css={SocialBtnStyle}
                  onClick={onClickNaverLogin}
                  className="naver"
                >
                  <img src={NaverLogoImgUrl} alt="네이버 로고" style={{ width: 'auto', height: '19px' }}/>
                </Button>
                {/*<Button*/}
                {/*  css={SocialBtnStyle}*/}
                {/*  onClick={onClickKakaoLogin}*/}
                {/*  className="kakao"*/}
                {/*>*/}
                {/*  <img src={KakaoLogoImgUrl} alt="카카오 로고" style={{ width: 'auto', height: '19px' }}/>*/}
                {/*</Button>*/}

                {isMobileApp || isAndroidInAppBrowser || isIosNaverInAppBrowser ? (
                  <Button
                    css={SocialBtnStyle}
                    style={{ background: 'white' }}
                    onClick={onClickGoogleLogInButton}
                    className="app google"
                  >
                    <GoogleIco/>
                  </Button>
                ) : (
                  <GoogleLogin
                    css={SocialBtnStyle}
                    clientId={googleClientId}
                    onSuccess={responseGoogle}
                    onFailure={responseGoogle}
                    className="google"
                  />
                )}

                {appleLoginEnabled && (<Button
                  css={SocialBtnStyle}
                  onClick={onClickAppleLogin}
                  className="apple"
                >
                  <img src={AppleLogoImgUrl} alt="애플 로고" style={{ width: 'auto', height: '21px' }}/>
                </Button>)}
              </div>

              <SocialLoginRealBtnWrap
                ref={naverLoginRealBtnWrapRef}
                onClick={onClickNaverLoginRealButton}
                id="naverIdLogin"
              />
            </SocialLogin>
          </Form.Item>
        </Form>
        <Helmet>
          <script>
            {`
              window.naverLogin = new window.naver.LoginWithNaverId(
              {
                clientId: '${naverClientId}',
                isPopup: true,
                callbackUrl: '${naverLoginCallbackUrl}/#/naverAuthResult',
                callbackHandle: false,
                loginButton: {color: 'green', type: 3, height: 60},
              })

              window.naverLogin.init()
              `
            }
          </script>
        </Helmet>
      </LoginWrap>
    </LoginPopupWrap>
  )
}

export default Form.create<FormComponentProps>()(observer(PopupLogin))
